import React, { useRef } from "react";
import { Form } from "react-bootstrap";
import { Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "../hooks/useGoogleMaps";

interface IProps {
    value: string;
    onChange: (value: string) => void;
    onPlaceSelected: (place: {
        address: string;
        city: string;
        latitude: number;
        longitude: number;
    }) => void;
    placeholder?: string;
    isInvalid?: boolean;
}

const GoogleMapsAutocomplete: React.FC<IProps> = ({
    value,
    onChange,
    onPlaceSelected,
    placeholder,
    isInvalid,
}) => {
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const { isLoaded } = useGoogleMaps();

    const handlePlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();

            // Check if place exists and has geometry (user selected from dropdown)
            if (!place || !place.geometry || !place.geometry.location) {
                return;
            }

            const address = place.formatted_address || "";
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            // Extract city from address components
            let city = "";
            if (place.address_components) {
                const cityComponent = place.address_components.find(
                    (component) =>
                        component.types.includes("locality") ||
                        component.types.includes("administrative_area_level_2")
                );
                city = cityComponent?.long_name || "";
            }

            onPlaceSelected({
                address,
                city,
                latitude,
                longitude,
            });
        }
    };

    const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    if (!isLoaded) {
        return (
            <Form.Control
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                isInvalid={isInvalid}
            />
        );
    }

    return (
        <Autocomplete onLoad={onLoad} onPlaceChanged={handlePlaceChanged}>
            <Form.Control
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                isInvalid={isInvalid}
            />
        </Autocomplete>
    );
};

export default GoogleMapsAutocomplete;
